// A DROPDOWN INSIDE A POP-UP CLOSES ON THE FIRST CLICK AWAY, AND ONLY THE
// DROPDOWN (owner, 2026-10-12 — D-351: "Clicking in and out of dropdowns is
// difficult and buggy").
//
// Two faults, both found by logging focus, clicks and `aria-expanded` in the
// Rules → Routing Filters dialog against the local mock:
//
// 1. A CLICK OUTSIDE THE POP-UP WHILE A LIST WAS OPEN CLOSED THE WHOLE POP-UP.
//    Fluent closes a list when its control loses focus, which happens on the
//    press; the release then lands on the dialog's backdrop, and the dialog
//    reads that as "close without saving". One click threw away the list AND
//    every unsaved choice in the pop-up. A person clicking away from a list
//    means "close the list", so the press that closes a list must not also
//    close the pop-up. `AppDialog` notes on every press whether a list was
//    open (`OPEN_POPUP_SELECTOR`) and ignores the backdrop click that follows
//    one (`keepDialogOpen`). Escape already behaved this way: Fluent's list
//    takes the first Escape and the dialog the second.
//
// 2. A CLICK ON AN OPEN DROPDOWN'S LABEL COULD NOT CLOSE IT. The press moved
//    focus off the control, which closed the list; the release then clicked
//    the label, and a label passes its click on to its control, which opened
//    the list again. Every click on the label read as "open", so the list
//    seemed stuck. `AppDialog` now cancels that passed-on click when the
//    label's control was open at the press (`labelledPopupControl`), so the
//    label closes an open list and opens a closed one, as the control itself
//    does, and puts focus back on the control.
//
// Pure, so the suite can check both rules without a page.

/** An open list's control: a Dropdown's button and a Combobox's input are
    both `role="combobox"`, and Fluent sets `aria-expanded` on each while its
    list shows. A Menu's trigger says so with `aria-haspopup`. Deliberately
    not every `aria-expanded="true"`: the invoice modal's sections carry it
    too, and an expanded section is not a list to close. */
export const OPEN_POPUP_SELECTOR =
  '[role="combobox"][aria-expanded="true"], [aria-haspopup="menu"][aria-expanded="true"], [aria-haspopup="listbox"][aria-expanded="true"]'

/** Whether to ignore a request to close the dialog: only a backdrop click, and
    only when the press that started it found a list open. */
export function keepDialogOpen(changeType: string, popupOpenAtPress: boolean): boolean {
  return changeType === 'backdropClick' && popupOpenAtPress
}

/** What `labelledPopupControl` needs from a press's target. */
interface LabelTargetLike {
  closest(selector: string): LabelLike | null
}
interface LabelLike {
  readonly control: PopupControlLike | null
}
export interface PopupControlLike {
  getAttribute(name: string): string | null
}

/**
 * The list control a pressed label names, or `null` when the press was not on
 * a label or the label names something that is not a list (a text box, a
 * checkbox — their labels are left to the browser).
 */
export function labelledPopupControl<T extends PopupControlLike>(target: unknown): T | null {
  if (target === null || typeof target !== 'object' || !('closest' in target)) return null
  const label = (target as LabelTargetLike).closest('label')
  const control = label?.control ?? null
  if (control === null || control.getAttribute('role') !== 'combobox') return null
  return control as T
}

/** Whether a list control is showing its list. */
export function isPopupOpen(control: PopupControlLike | null): boolean {
  return control?.getAttribute('aria-expanded') === 'true'
}
